/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../../_metronic/layout/core'
import { shallowEqual, useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import * as actions from "./_redux/dashboard/Actions";

import {
  MixedWidget10,
  MixedWidget11,
  ListsWidget5,
  StatisticsWidget5,
} from '../../../_metronic/partials/widgets'

interface DashboardData {
  activeAlerts: number;
  aiAccuracy: number;
  activeStreams: number;
  lastEventTime: string | null;
  totalAlerts: number;
  dashboardMetrics: {
    currentPeriod: {
      today: number;
      yesterday: number;
      week: number;
      month: number;
      unacknowledged: number;
    };
    eventsByType: any[];
    insights: any[];
    analytics: {
      daily: any;
      weekly: any;
      monthly: any;
    };
  };
}

interface DashboardPageProps {
  dashboard_data?: DashboardData;
  events_data?: any
}

const DashboardPage: FC<DashboardPageProps> = ({ dashboard_data, events_data }) => {
  // Format time difference for "Last Event"
  const formatLastEventTime = () => {
    if (!dashboard_data?.lastEventTime) return 'No events';
    
    const lastEvent = new Date(dashboard_data.lastEventTime);
    const now = new Date();
    const diffMs = now.getTime() - lastEvent.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) {
      return `${diffMins} min ago`;
    } else if (diffMins < 1440) {
      return `${Math.floor(diffMins / 60)} hours ago`;
    } else {
      return `${Math.floor(diffMins / 1440)} days ago`;
    }
  };

  // Get chart data for MixedWidget10 from weekly analytics
  const getWeeklyChartData = () => {
    const weeklyData = dashboard_data?.dashboardMetrics?.analytics?.weekly?.metrics?.eventsByDay || [0, 0, 0, 0, 0, 0, 0];
    
    return {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      datasets: [{
        data: weeklyData,
        backgroundColor: 'transparent',
        borderColor: '#3E97FF',
        pointBackgroundColor: '#3E97FF',
        pointBorderColor: '#ffffff',
        pointHoverBackgroundColor: '#ffffff',
        pointHoverBorderColor: '#3E97FF',
        pointRadius: 4,
        pointHoverRadius: 6,
      }]
    };
  };

  // Get chart data for MixedWidget11 from hourly analytics
  const getHourlyChartData = () => {
    const hourlyData = dashboard_data?.dashboardMetrics?.analytics?.weekly?.metrics?.eventsByHour || 
      Array(24).fill(0);
    
    return {
      labels: Array.from({length: 24}, (_, i) => `${i}:00`),
      datasets: [{
        data: hourlyData,
        backgroundColor: 'transparent',
        borderColor: '#3E97FF',
        pointBackgroundColor: '#3E97FF',
        pointBorderColor: '#ffffff',
        pointHoverBackgroundColor: '#ffffff',
        pointHoverBorderColor: '#3E97FF',
        pointRadius: 3,
        pointHoverRadius: 4,
      }]
    };
  };

  return (
    <>
      {/* begin::Row */}
      <div className='row g-5 g-xl-8'>
        <div className='col-xl-3'>
          <StatisticsWidget5
            className='card-xl-stretch mb-xl-8'
            svgIcon='/media/icons/duotune/general/gen032.svg'
            color='white'
            iconColor='primary'
            title={dashboard_data?.activeAlerts?.toString() || '0'}
            description='Active Alerts'
          />
        </div>

        <div className='col-xl-3'>
          <StatisticsWidget5
            className='card-xl-stretch mb-xl-8'
            svgIcon='/media/icons/duotune/ecommerce/ecm008.svg'
            color='dark'
            iconColor='white'
            title={`${dashboard_data?.aiAccuracy || 0}%`}
            description='AI Accuracy'
          />
        </div>

        <div className='col-xl-3'>
          <StatisticsWidget5
            className='card-xl-stretch mb-xl-8'
            svgIcon='/media/icons/duotune/finance/fin006.svg'
            color='warning'
            iconColor='white'
            title={dashboard_data?.activeStreams?.toString() || '0'}
            description='Live Streams'
          />
        </div>

        <div className='col-xl-3'>
          <StatisticsWidget5
            className='card-xl-stretch mb-5 mb-xl-8'
            svgIcon='/media/icons/duotune/graphs/gra007.svg'
            color='info'
            iconColor='white'
            title={formatLastEventTime()}
            description='Last Event'
          />
        </div>
      </div>

      <div className='row gy-5 g-xl-8 align-items-start'>
        <div className='col-xxl-8'>
          {events_data &&
          <ListsWidget5 
            className='card-xxl-stretch' 
            insights={dashboard_data?.dashboardMetrics?.insights || []}
            eventsByType={dashboard_data?.dashboardMetrics?.eventsByType || []}
            events_data = {events_data}
          />}
        </div>
        <div className='col-xxl-4 d-flex flex-column'>
          <MixedWidget10
            className='mb-5 mb-xl-8'
            chartColor='primary'
            chartHeight='150px'
            chartData={getWeeklyChartData()}
            title="Weekly Events"
            stats={dashboard_data?.dashboardMetrics?.currentPeriod?.week || 0}
          />
          <MixedWidget11
            className='mb-5 mb-xl-8'
            chartColor='primary'
            chartHeight='175px'
            chartData={getHourlyChartData()}
            title="Hourly Distribution"
            description="Events by hour of day"
          />
        </div>
      </div>
    </>
  );
};

const DashboardWrapper: FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { business, dashboard_data, events_data } = useSelector(
    (state: any) => ({
      business: state.auth.user?.activeBusiness,
      dashboard_data: state.dashboard.dashboard_data,
      events_data: state.dashboard.events_data
    }),
    shallowEqual
  );

  useEffect(() => {
    if (business?.id) {
      dispatch(actions.fetchAllData('dashboard_data', business.id));
      dispatch(actions.fetchAllData('events', `?business=${business.id}`));
    }
  }, [business, dispatch]);

  useEffect(() => {
    console.log(events_data)
  }, [events_data])

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.DASHBOARD'})}</PageTitle>
      <DashboardPage events_data = {events_data} dashboard_data={dashboard_data} />
    </>
  );
};

export {DashboardWrapper};